/**
 * Skins: what your character looks like, and what rubies are for.
 *
 * Shared by the client and the server because the server is the one that
 * decides whether you can afford something. A price that only the browser knew
 * would be a price nobody paid.
 *
 * Every skin is DESCRIBED here and DRAWN by the renderer, so there are still no
 * image assets: a beach ball is four coloured wedges and an emoji is a glyph the
 * font already has. Twenty four skins cost the bundle about two kilobytes.
 */
/**
 * EVERY skin is locked until it is bought. There are no free ones.
 *
 * A player who owns nothing is not left blank: they keep the seat colour the
 * arena has always assigned, which is what still lets five people tell each
 * other apart in a lobby where nobody has spent a ruby yet. A skin REPLACES
 * that colour rather than being the only way to have one, so the shop is
 * something to want rather than something you need before you can play.
 */
const CHEAP = [
    { id: 'neon-cyan', name: 'Néon cyan', cost: 5, kind: 'neon', base: 0x22d3ee, accent: 0xffffff, glow: 0x22d3ee },
    { id: 'neon-magenta', name: 'Néon magenta', cost: 5, kind: 'neon', base: 0xff2d95, accent: 0xffffff, glow: 0xff2d95 },
    { id: 'neon-lime', name: 'Néon vert', cost: 5, kind: 'neon', base: 0x4ade80, accent: 0xffffff, glow: 0x4ade80 },
    { id: 'neon-violet', name: 'Néon violet', cost: 5, kind: 'neon', base: 0xa78bfa, accent: 0xffffff, glow: 0xa78bfa },
];
/**
 * Prices rise with how much the skin stands out rather than with how hard it
 * was to draw. A plain ball is cheap; something that reads across the arena at
 * a glance is the thing worth saving for.
 */
const UNLOCKABLE = [
    // ---- balls
    { id: 'tennis', name: 'Balle de tennis', cost: 10, kind: 'ball', pattern: 'tennis', base: 0xd4f036, accent: 0xffffff, glow: 0xd4f036 },
    { id: 'basketball', name: 'Ballon de basket', cost: 10, kind: 'ball', pattern: 'basketball', base: 0xf97316, accent: 0x1a1005, glow: 0xf97316 },
    { id: 'football', name: 'Ballon de foot', cost: 12, kind: 'ball', pattern: 'football', base: 0xf8fafc, accent: 0x11161f, glow: 0xf8fafc },
    { id: 'beach', name: 'Ballon de plage', cost: 14, kind: 'ball', pattern: 'beach', base: 0xffffff, accent: 0xff2d95, glow: 0xffd9ec },
    { id: 'eight-ball', name: 'Boule numéro 8', cost: 16, kind: 'ball', pattern: 'eight', base: 0x14121a, accent: 0xffffff, glow: 0x9ca3af },
    { id: 'bowling', name: 'Boule de bowling', cost: 16, kind: 'ball', pattern: 'bowling', base: 0x8b5cf6, accent: 0x2e1065, glow: 0xa78bfa },
    // ---- faces
    { id: 'smiley', name: 'Smiley', cost: 12, kind: 'face', mood: 'happy', base: 0xfacc15, accent: 0x1a1005, glow: 0xfde047 },
    { id: 'smiley-cool', name: 'Smiley lunettes', cost: 18, kind: 'face', mood: 'cool', base: 0xfacc15, accent: 0x11161f, glow: 0xfde047 },
    { id: 'smiley-wink', name: 'Smiley clin d’œil', cost: 18, kind: 'face', mood: 'wink', base: 0xfb7185, accent: 0x1a0508, glow: 0xfda4af },
    { id: 'smiley-angry', name: 'Smiley fâché', cost: 20, kind: 'face', mood: 'angry', base: 0xef4444, accent: 0x1a0505, glow: 0xf87171 },
    { id: 'smiley-sleepy', name: 'Smiley endormi', cost: 20, kind: 'face', mood: 'sleepy', base: 0x93c5fd, accent: 0x0b1220, glow: 0xbfdbfe },
    // ---- glyphs
    { id: 'fire', name: 'Feu', cost: 22, kind: 'emoji', glyph: '\u{1F525}', base: 0x7c2d12, accent: 0xfb923c, glow: 0xfb923c },
    { id: 'star', name: 'Étoile', cost: 22, kind: 'emoji', glyph: '⭐', base: 0x422006, accent: 0xfacc15, glow: 0xfacc15 },
    { id: 'ghost', name: 'Fantôme', cost: 25, kind: 'emoji', glyph: '\u{1F47B}', base: 0x312e81, accent: 0xe0e7ff, glow: 0xc7d2fe },
    { id: 'alien', name: 'Alien', cost: 25, kind: 'emoji', glyph: '\u{1F47D}', base: 0x052e16, accent: 0x86efac, glow: 0x86efac },
    { id: 'robot', name: 'Robot', cost: 28, kind: 'emoji', glyph: '\u{1F916}', base: 0x0c4a6e, accent: 0x7dd3fc, glow: 0x7dd3fc },
    { id: 'cat', name: 'Chat', cost: 28, kind: 'emoji', glyph: '\u{1F431}', base: 0x431407, accent: 0xfdba74, glow: 0xfdba74 },
    { id: 'frog', name: 'Grenouille', cost: 30, kind: 'emoji', glyph: '\u{1F438}', base: 0x14532d, accent: 0xbbf7d0, glow: 0x4ade80 },
    { id: 'rocket', name: 'Fusée', cost: 32, kind: 'emoji', glyph: '\u{1F680}', base: 0x1e1b4b, accent: 0xa5b4fc, glow: 0x818cf8 },
    { id: 'diamond', name: 'Diamant', cost: 40, kind: 'emoji', glyph: '\u{1F48E}', base: 0x083344, accent: 0x67e8f9, glow: 0x22d3ee },
];
export const SKINS = [...CHEAP, ...UNLOCKABLE];
const BY_ID = new Map(SKINS.map((s) => [s.id, s]));
/**
 * Wearing nothing is a valid state, and the one everybody starts in.
 *
 * The renderer falls back to the seat colour for it, so an unskinned player
 * looks exactly like the game did before skins existed.
 */
export const NO_SKIN = '';
export function skinById(id) {
    return (id ? BY_ID.get(id) : null) ?? null;
}
/** Owned by everybody from the start. Deliberately empty: all skins are locked. */
export function starterSkins() {
    return [];
}
export function skinCost(id) {
    return BY_ID.get(id)?.cost ?? null;
}
/**
 * Rubies awarded for a finished ONLINE match.
 *
 * The cheapest skin is 5, so one win or five games played buys something. That
 * is deliberate: the first purchase should be reachable in an evening, and the
 * dearest skin should take a while.
 *
 * Winning pays several times what turning up pays, but turning up still pays,
 * because a nine year old who loses five matches in a row should still be
 * saving toward something. Solo games pay on a new personal best only, which is
 * self limiting: your tenth best at Snake is much harder than your first.
 */
export const RUBIES_FOR_WIN = 5;
export const RUBIES_FOR_PLAYING = 1;
export const RUBIES_FOR_PERSONAL_BEST = 1;
/**
 * Winning a match padded out with AI.
 *
 * It pays, because a child playing alone against the bots for three minutes and
 * being given nothing looks exactly like the reward being broken. It pays LESS
 * than a real win, because a match against bots can be started again
 * immediately and forever, and the difference is the whole reason a win against
 * people is worth wanting.
 *
 * Note this is only about RUBIES. A bot padded match still moves nobody's
 * rating, because farming a leaderboard and earning pocket money are different
 * risks and only one of them spoils the game for everybody else.
 */
export const RUBIES_FOR_PRACTICE_WIN = 2;
/**
 * The most a single solo game will ever pay in personal bests.
 *
 * Without a cap this is the cheapest exploit in the product: most solo games
 * have a wide score range and the client reports its own score, so posting 1,
 * 2, 3 and so on at Snake pays a ruby each time. Measured at the current rate
 * limit that is 119 rubies a minute, which buys the entire 438 ruby catalogue
 * in under four minutes.
 *
 * A cap rather than a cleverer rule because it cannot be gamed by pacing: five
 * per game, twenty games, so a hundred rubies is the lifetime ceiling from
 * playing alone. Enough to matter, not enough to skip the multiplayer entirely.
 */
export const MAX_PAID_BESTS_PER_GAME = 5;
/**
 * Every id must be unique and every price sane, checked at import time.
 * A duplicate would let one skin be bought and a different one delivered.
 */
const seen = new Set();
for (const skin of SKINS) {
    if (seen.has(skin.id))
        throw new Error(`duplicate skin id: ${skin.id}`);
    seen.add(skin.id);
    if (skin.cost < 0 || !Number.isInteger(skin.cost)) {
        throw new Error(`skin ${skin.id} has a strange price`);
    }
}
//# sourceMappingURL=skins.js.map